import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/format';
import { useBanks } from '@/hooks/useBanks';
import type { ClientAccount } from '@/hooks/useClientAuth';
import type { WithdrawalDraft } from '@/hooks/useWithdrawalFlow';

type WithdrawalFormProps = {
  client: ClientAccount;
  onSubmit: (draft: WithdrawalDraft) => void;
};

type FormState = {
  fullName: string;
  bankId: string;
  iban: string;
  phone: string;
  email: string;
};

type FieldKey = keyof FormState;

const initialState: FormState = {
  fullName: '',
  bankId: '',
  iban: '',
  phone: '',
  email: '',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function WithdrawalForm({ client, onSubmit }: WithdrawalFormProps) {
  const { t } = useTranslation();
  const { banks, loading, error: banksError } = useBanks();
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const update = (key: FieldKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): WithdrawalDraft | null => {
    const next: Partial<Record<FieldKey, string>> = {};
    (['fullName', 'bankId', 'iban', 'phone'] as FieldKey[]).forEach((key) => {
      if (!form[key].trim()) next[key] = t('withdrawal.errorRequired');
    });
    if (!form.email.trim()) next.email = t('withdrawal.errorRequired');
    else if (!emailPattern.test(form.email.trim())) next.email = t('withdrawal.errorEmail');

    const bank = banks.find((entry) => entry.id === form.bankId);
    if (form.bankId.trim() && !bank) next.bankId = t('withdrawal.errorRequired');

    setErrors(next);
    if (Object.keys(next).length > 0 || !bank) return null;

    return {
      fullName: form.fullName.trim(),
      bankId: bank.id,
      bankName: bank.name,
      bankShort: bank.short || bank.name.slice(0, 3).toUpperCase(),
      iban: form.iban.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      // Le montant du retrait correspond à la totalité du solde du client.
      amount: client.balance,
    };
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const draft = validate();
    if (draft) onSubmit(draft);
  };

  const textFields: {
    key: FieldKey;
    label: string;
    placeholder: string;
    icon: string;
    wide?: boolean;
    inputMode?: 'text' | 'tel' | 'email';
    type?: string;
  }[] = [
    {
      key: 'fullName',
      label: t('withdrawal.fullName'),
      placeholder: t('withdrawal.fullNamePh'),
      icon: 'ri-user-line',
      wide: true,
    },
    {
      key: 'iban',
      label: t('withdrawal.iban'),
      placeholder: t('withdrawal.ibanPh'),
      icon: 'ri-numbers-line',
    },
    {
      key: 'phone',
      label: t('withdrawal.phone'),
      placeholder: t('withdrawal.phonePh'),
      icon: 'ri-phone-line',
      inputMode: 'tel',
    },
    {
      key: 'email',
      label: t('withdrawal.email'),
      placeholder: t('withdrawal.emailPh'),
      icon: 'ri-mail-line',
      inputMode: 'email',
      type: 'email',
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="relative flex flex-wrap items-center justify-between gap-4 overflow-hidden rounded-xl bg-gradient-to-r from-primary-800 via-primary-700 to-primary-500 px-5 py-4">
        <span className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-background-50/10"></span>
        <span className="pointer-events-none absolute -bottom-12 right-24 h-24 w-24 rounded-full bg-primary-400/20"></span>
        <div className="relative flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-background-50/20 text-background-50">
            <i className="ri-wallet-3-line text-xl"></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-background-50">{t('withdrawal.available')}</p>
            <p className="text-xs text-background-50/75">{t('withdrawal.availableHint')}</p>
          </div>
        </div>
        <span className="relative font-heading text-xl font-bold tracking-tight text-background-50 md:text-2xl">
          {formatCurrency(client.balance, client.currency)}
        </span>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-user-3-line text-lg text-primary-700"></i>
          {t('withdrawal.sectionBeneficiary')}
        </h2>
        <p className="mt-1 text-sm text-foreground-500">
          {t('withdrawal.sectionBank')} · {t('withdrawal.sectionContact')}
        </p>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label
              htmlFor="bankId"
              className="mb-2 block text-sm font-semibold text-foreground-800"
            >
              {t('withdrawal.bankSelect')} <span className="text-secondary-600">*</span>
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-bank-line text-lg"></i>
              </span>
              <select
                id="bankId"
                name="bankId"
                value={form.bankId}
                onChange={(event) => update('bankId', event.target.value)}
                disabled={loading}
                className={`w-full cursor-pointer appearance-none rounded-md border bg-background-50 py-3 pl-12 pr-10 text-sm outline-none transition-colors focus:ring-2 ${
                  form.bankId ? 'text-foreground-950' : 'text-foreground-400'
                } ${
                  errors.bankId
                    ? 'border-secondary-400 focus:border-secondary-500 focus:ring-secondary-200'
                    : 'border-background-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
              >
                <option value="">
                  {loading ? t('common.loading') : t('withdrawal.bankSelectPlaceholder')}
                </option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-arrow-down-s-line text-xl"></i>
              </span>
            </div>
            {errors.bankId && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
                <i className="ri-error-warning-line"></i>
                {errors.bankId}
              </p>
            )}
          </div>

          {textFields.map((field) => (
            <div key={field.key} className={field.wide ? 'sm:col-span-2' : ''}>
              <label
                htmlFor={field.key}
                className="mb-2 block text-sm font-semibold text-foreground-800"
              >
                {field.label} <span className="text-secondary-600">*</span>
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className={`${field.icon} text-lg`}></i>
                </span>
                <input
                  id={field.key}
                  name={field.key}
                  type={field.type ?? 'text'}
                  inputMode={field.inputMode ?? 'text'}
                  autoComplete="off"
                  value={form[field.key]}
                  onChange={(event) => update(field.key, event.target.value)}
                  placeholder={field.placeholder}
                  className={`w-full rounded-md border bg-background-50 py-3 pl-12 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:ring-2 ${
                    errors[field.key]
                      ? 'border-secondary-400 focus:border-secondary-500 focus:ring-secondary-200'
                      : 'border-background-300 focus:border-primary-500 focus:ring-primary-200'
                  }`}
                />
              </div>
              {errors[field.key] && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
                  <i className="ri-error-warning-line"></i>
                  {errors[field.key]}
                </p>
              )}
            </div>
          ))}

          {banksError && (
            <div className="sm:col-span-2 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
              <i className="ri-error-warning-line mt-0.5 text-base"></i>
              <span>{banksError}</span>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-money-dollar-circle-line text-lg text-primary-700"></i>
          {t('withdrawal.sectionAmount')}
        </h2>

        <div className="relative mt-4 flex flex-wrap items-center justify-between gap-4 overflow-hidden rounded-xl bg-gradient-to-r from-primary-800 via-primary-700 to-primary-500 px-5 py-4">
          <span className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-background-50/10"></span>
          <span className="pointer-events-none absolute -bottom-12 right-24 h-24 w-24 rounded-full bg-primary-400/20"></span>
          <div className="relative flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-background-50/20 text-background-50">
              <i className="ri-lock-2-line text-xl"></i>
            </span>
            <p className="text-sm font-semibold text-background-50">{t('withdrawal.amount')}</p>
          </div>
          <span className="relative font-heading text-xl font-bold tracking-tight text-background-50 md:text-2xl">
            {formatCurrency(client.balance, client.currency)}
          </span>
        </div>
        <p className="mt-2 flex items-start gap-2 text-xs text-foreground-500">
          <i className="ri-information-line mt-0.5"></i>
          {t('withdrawal.amountReadonly')}
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-accent-200 bg-accent-50 px-5 py-4 text-sm text-foreground-700">
        <i className="ri-shield-check-line mt-0.5 text-base text-accent-700"></i>
        <span>{t('withdrawal.secureNote')}</span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="submit"
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-check-line text-lg"></i>
          {t('withdrawal.submit')}
        </button>
        <a
          href="/dashboard"
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          <i className="ri-close-line text-lg"></i>
          {t('withdrawal.cancel')}
        </a>
      </div>
    </form>
  );
}